import React, { useEffect, useState } from "react";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import { MapContainer, TileLayer, Popup } from "react-leaflet";
import { FaTree, FaMapMarkerAlt, FaCalendarPlus, FaCalendarAlt, FaClock, FaPagelines, FaUserAlt, FaFilePdf, FaCheckCircle } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "leaflet/dist/leaflet.css";
import "../assets/NewFiles/Css/ProfilePage2.css";
import "../assets/NewFiles/Css/ProjectPage.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useParams } from "react-router-dom";
import api from "../axios/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ReactLoading from "react-loading";
import {
  EmailShareButton,
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
} from "react-share";
import {
  EmailIcon,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from "react-share";
import { FaInstagram } from "react-icons/fa";
import Grid from "../components/common/Grid";

import {
  CO2_TO_O2_RATIO,
  getSpeciesLabel,
  getTreeCO2e,
  buildCumulativeCO2ChartData,
  getDefaultChartData,
  getBase64ImageFromUrl,
} from "../utils";

const verifiedIcon = new L.Icon({
  iconUrl: "/images/map_pin_icon.png",
  iconSize: [36, 48],
  iconAnchor: [18, 48],
  popupAnchor: [0, -45],
});

const unverifiedIcon = new L.Icon({
  iconUrl: "/images/map_pin_icon.png",
  iconSize: [36, 48],
  iconAnchor: [18, 48],
  popupAnchor: [0, -45],
});

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const mockData = {
  treesByMonth: {
    labels: [
      "2021-1",
      "2021-6",
      "2021-11",
      "2022-4",
      "2022-9",
      "2023-2",
      "2023-7",
      "2023-12",
      "2024-5",
    ],
    data: [300, 3200, 700, 900, 800, 400, 300, 500, 200],
  },
  co2OffsetByMonth: {
    labels: [
      "2021-1",
      "2021-6",
      "2021-11",
      "2022-4",
      "2022-9",
      "2023-2",
      "2023-7",
      "2023-12",
      "2024-5",
    ],
    data: [20, 40, 80, 120, 160, 220, 300, 380, 460],
  },
  position: [20.5937, 78.9629],
  averageWeeklyTrees: {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    data: [120, 150, 180, 130, 160, 90, 110],
  },
};

export default function UserImpactDashboard() {
  const { clientId } = useParams();
  const [loading, setLoading] = useState(true);

  const [clientData, setClientData] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const co2ChartRef = React.useRef(null);
  const treesByMonthChartRef = React.useRef(null);
  const weeklyTreesChartRef = React.useRef(null);
  const plantationStatusChartRef = React.useRef(null);
  const totalTreesChartRef = React.useRef(null);

  const getSpeciesDataMap = (trees) => {
    if (!trees || trees.length === 0) return {};
    const now = new Date();
    return trees.reduce((acc, tree) => {
      const species = getSpeciesLabel(tree);
      if (!acc[species]) {
        acc[species] = {
          count: 0,
          icon: tree.species_icon_url || tree.icon_url || tree.treeImageUrl || tree.treeimageUrl || '/images/icon/leaf.png',
          speciesId: tree.tree_species,
          co2: 0,
          o2: 0,
        };
      }
      acc[species].count++;
      const treeCO2 = getTreeCO2e(tree, now);
      acc[species].co2 += treeCO2;
      acc[species].o2 += treeCO2 * CO2_TO_O2_RATIO;
      return acc;
    }, {});
  };

  const getSpeciesDistribution = (trees) => {
    if (!trees) return [];
    const dist = {};
    trees.forEach(tree => {
      const species = getSpeciesLabel(tree);
      dist[species] = (dist[species] || 0) + 1;
    });
    return Object.keys(dist).sort().map(key => [key, dist[key]]);
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 15;
      let currentY = 15;

      const addHeader = (titleText) => {
        doc.setFillColor(27, 67, 50);
        doc.rect(margin, currentY, pageWidth - 2 * margin, 12, 'F');
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text(titleText, margin + 5, currentY + 8);
        currentY += 18;
      };

      const checkPageBreak = (heightNeeded) => {
        if (currentY + heightNeeded > pageHeight - margin - 15) {
          doc.addPage();
          currentY = margin;
          return true;
        }
        return false;
      };

      const clientName = clientData?.full_name || 'Client';

      // PAGE 1: TITLE & COVER IMAGE & PROFILE DETAILS
      doc.setFillColor(27, 67, 50);
      doc.rect(margin, currentY, pageWidth - 2 * margin, 25, 'F');

      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(200, 220, 200);
      doc.text('ARANYASETU USER GREEN COVER IMPACT REPORT', margin + 6, currentY + 8);

      const splitTitle = doc.splitTextToSize(`Dashboard: ${clientName}`, pageWidth - 2 * margin - 12);
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text(splitTitle, margin + 6, currentY + 16);
      currentY += 32;

      // Cover Pic
      if (clientData?.userCoverPic) {
        const coverImgBase64 = await getBase64ImageFromUrl(clientData.userCoverPic);
        if (coverImgBase64) {
          doc.addImage(coverImgBase64, 'JPEG', margin, currentY, pageWidth - 2 * margin, 60, undefined, 'FAST');
          doc.setDrawColor(27, 67, 50);
          doc.setLineWidth(0.3);
          doc.rect(margin, currentY, pageWidth - 2 * margin, 60);
          currentY += 67;
        }
      }

      // Profile details table
      const profileData = [
        ['Client Name', clientData?.full_name || 'N/A'],
        ['User Type', clientData?.role_code || 'N/A'],
        ['Client Since', formatDateUs(clientData?.createdAt)]
      ];

      autoTable(doc, {
        startY: currentY,
        body: profileData,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 0, textColor: [60, 60, 60], font: 'Helvetica' },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [27, 67, 50], width: 45 },
          1: { width: 135 }
        },
        margin: { left: margin, right: margin }
      });
      currentY = doc.lastAutoTable.finalY + 15;

      // PAGE 2: PLANTATION SUMMARY & STATISTICS
      checkPageBreak(50);
      addHeader('Plantation Status & Summary');

      const plantationSummary = [
        ['Total Planted Trees', `${treeData.length || 0} Trees`],
        ['Verified Trees', `${clientData.verifiedTrees || 0} Trees`],
        ['Geolocated Trees', `${clientData.geoLocatedTreesCount || 0} Trees`],
        ['Average Weekly Trees', `${averageWeeklyCount} Trees`]
      ];

      autoTable(doc, {
        startY: currentY,
        body: plantationSummary,
        theme: 'striped',
        styles: { fontSize: 10, cellPadding: 0, font: 'Helvetica' },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [27, 67, 50], width: 60 },
          1: { width: 120 }
        },
        margin: { left: margin, right: margin }
      });
      currentY = doc.lastAutoTable.finalY + 15;

      // Cumulative CO2 Offset & O2 Produced by Month (Only render if there is data)
      if (co2ChartResult) {
        checkPageBreak(95);
        addHeader('Total Cumulative CO2 Offset & O2 Produced by Month');

        const summaryText = `Total CO2e Stored: ${co2ChartResult.totalCO2e >= 1000 ? (co2ChartResult.totalCO2e / 1000).toFixed(2) + ' t' : co2ChartResult.totalCO2e + ' kg'}   |   Total O2 Produced: ${co2ChartResult.totalO2 >= 1000 ? (co2ChartResult.totalO2 / 1000).toFixed(2) + ' t' : co2ChartResult.totalO2 + ' kg'}   |   Trees with DBH: ${co2ChartResult.treesWithDBH} / ${co2ChartResult.totalTrees}`;
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(27, 67, 50);
        doc.text(summaryText, margin, currentY);
        currentY += 6;

        if (co2ChartRef.current) {
          try {
            const co2Img = co2ChartRef.current.toBase64Image();
            if (co2Img) {
              doc.addImage(co2Img, 'PNG', margin, currentY, pageWidth - 2 * margin, 70, undefined, 'FAST');
              currentY += 75;
            }
          } catch (e) {
            console.error("Failed to capture CO2 Chart:", e);
          }
        }
        currentY += 5;
      }

      // PAGE 3: CHARTS & SPECIES DISTRIBUTION
      // Trees by Month
      if (treeData && treeData.length > 0) {
        const treesByYearResult = calculateTreesByYear(treeData);
        if (treesByYearResult && treesByYearResult.labels.length > 0) {
          checkPageBreak(95);
          addHeader('Total Trees Registered by Month');

          if (treesByMonthChartRef.current) {
            try {
              const chartImg = treesByMonthChartRef.current.toBase64Image();
              if (chartImg) {
                doc.addImage(chartImg, 'PNG', margin, currentY, pageWidth - 2 * margin, 70, undefined, 'FAST');
                currentY += 75;
              }
            } catch (e) {
              console.error("Failed to capture Trees by Month Chart:", e);
            }
          }
          currentY += 5;
        }
      }

      // Average Weekly Trees
      const weeklyTreesResult = calculateAverageWeeklyTrees(treeData);
      if (weeklyTreesResult && weeklyTreesResult.data.some(v => v > 0)) {
        checkPageBreak(95);
        addHeader('Average Weekly Trees (Last 7 Days)');

        if (weeklyTreesChartRef.current) {
          try {
            const chartImg = weeklyTreesChartRef.current.toBase64Image();
            if (chartImg) {
              doc.addImage(chartImg, 'PNG', margin, currentY, pageWidth - 2 * margin, 70, undefined, 'FAST');
              currentY += 75;
            }
          } catch (e) {
            console.error("Failed to capture Weekly Trees Chart:", e);
          }
        }
        currentY += 5;
      }

      // Species-wise Tree Distribution (replacing interactive map)
      const speciesMap = getSpeciesDataMap(treeData);
      const speciesKeys = Object.keys(speciesMap).sort((a, b) => a.localeCompare(b));
      if (speciesKeys.length > 0) {
        checkPageBreak(50);
        addHeader('Species-wise Tree Distribution');

        const speciesRows = speciesKeys.map((specKey, idx) => {
          const item = speciesMap[specKey];
          const co2Val = item.co2 >= 1000 ? `${(item.co2 / 1000).toFixed(2)} t` : `${(item.co2 || 0).toFixed(2)} kg`;
          const o2Val = item.o2 >= 1000 ? `${(item.o2 / 1000).toFixed(2)} t` : `${(item.o2 || 0).toFixed(2)} kg`;
          return [idx + 1, specKey, item.count, co2Val, o2Val];
        });

        const totalPdfCount = Object.values(speciesMap).reduce((sum, item) => sum + item.count, 0);
        const totalPdfCo2 = Object.values(speciesMap).reduce((sum, item) => sum + (item.co2 || 0), 0);
        const totalPdfO2 = Object.values(speciesMap).reduce((sum, item) => sum + (item.o2 || 0), 0);

        const totalPdfCo2Str = totalPdfCo2 >= 1000 ? `${(totalPdfCo2 / 1000).toFixed(2)} t` : `${totalPdfCo2.toFixed(2)} kg`;
        const totalPdfO2Str = totalPdfO2 >= 1000 ? `${(totalPdfO2 / 1000).toFixed(2)} t` : `${totalPdfO2.toFixed(2)} kg`;

        autoTable(doc, {
          startY: currentY,
          head: [['Sr. No.', 'Species Name', 'Tree Count', 'CO2 Sequestration', 'O2 produce']],
          body: speciesRows,
          foot: [['', 'Total', totalPdfCount, totalPdfCo2Str, totalPdfO2Str]],
          showFoot: 'lastPage',
          theme: 'striped',
          styles: { fontSize: 9, font: 'Helvetica' },
          headStyles: { fillColor: [27, 67, 50], textColor: [255, 255, 255] },
          footStyles: { fillColor: [230, 245, 235], textColor: [27, 67, 50], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' }
          },
          margin: { left: margin, right: margin }
        });
        currentY = doc.lastAutoTable.finalY + 15;
      }

      // Footers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);

        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 15, pageHeight - 8);
        doc.text(`Aranyasetu Client: ${clientName}`, margin, pageHeight - 8);

        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.1);
        doc.line(margin, pageHeight - 11, pageWidth - margin, pageHeight - 11);
      }

      const pdfFileName = `${(clientName).replace(/[^a-z0-9]/gi, '_').toLowerCase()}_impact_report.pdf`;
      doc.save(pdfFileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };
  const calculateAverageWeeklyTrees = (data) => {
    const averageWeeklyTrees = {
      labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      data: [0, 0, 0, 0, 0, 0, 0],
    };

    const dayOfWeekMap = {
      0: 6,
      1: 0,
      2: 1,
      3: 2,
      4: 3,
      5: 4,
      6: 5,
    };

    // Get the current date and subtract 7 days to cover the past week
    const currentDate = new Date();
    const startDate = new Date();
    startDate.setDate(currentDate.getDate() - 7);

    const dayCount = Array(7).fill(0);

    // Iterate through the data and calculate trees planted per day
    data.forEach((tree) => {
      const plantedOn = new Date(tree.plantedOn);
      if (plantedOn >= startDate && plantedOn <= currentDate) {
        const dayOfWeek = plantedOn.getDay();
        const index = dayOfWeekMap[dayOfWeek];
        averageWeeklyTrees.data[index]++;
        dayCount[index]++;
      }
    });

    // Calculate the average by dividing the sum by the number of trees per day
    averageWeeklyTrees.data = averageWeeklyTrees.data.map((count, index) => {
      return dayCount[index] === 0 ? 0 : count;
    });
    return averageWeeklyTrees;
  };

  const calculateTreesByYear = (data) => {
    const treesByMonth = {
      labels: [],
      data: [],
    };

    // Get the current year and month
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const startYear = currentYear - 10; // Last 10 years

    const treeCountByMonth = {};

    data.forEach((tree) => {
      const plantedOn = new Date(tree.plantedOn);
      const year = plantedOn.getFullYear();
      const month = plantedOn.getMonth() + 1; // Adding 1 since getMonth is 0-indexed

      // Only consider data within the last 10 years
      if (year >= startYear) {
        const yearMonthKey = `${year}-${month}`;

        if (!treeCountByMonth[yearMonthKey]) {
          treeCountByMonth[yearMonthKey] = 0;
        }
        treeCountByMonth[yearMonthKey]++;
      }
    });

    // Convert the object into an ordered list of labels and data
    Object.keys(treeCountByMonth)
      .sort()
      .forEach((key) => {
        treesByMonth.labels.push(key);
        treesByMonth.data.push(treeCountByMonth[key]);
      });
    return treesByMonth;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`/api/upload/userData/${clientId}`);
        if (!response.data || response.data.length === 0) {
          setError("No data available for this client.");
        } else {
          setClientData(response.data.Userdata[0]);
          setTreeData(response.data.Treesdata);
          setCompanyData(response.data.Companydata[0]);
        }
      } catch (error) {
        setError("Failed to fetch data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [clientId]);

  const co2ChartResult = buildCumulativeCO2ChartData(treeData);
  const weeklyTreesData = calculateAverageWeeklyTrees(treeData);
  const averageWeeklyCount = weeklyTreesData.data.length > 0
    ? Math.round(weeklyTreesData.data.reduce((a, b) => a + b, 0) / 7)
    : 0;

  const co2ChartData = co2ChartResult ? {
    labels: co2ChartResult.labels,
    datasets: [
      {
        label: "Cumulative CO2e (kg)",
        data: co2ChartResult.data,
        borderColor: "#4CAF50",
        backgroundColor: "rgba(76, 175, 80, 0.1)",
        fill: true,
        tension: 0.3,
        pointRadius: 2,
        pointBackgroundColor: "#4CAF50",
      },
      {
        label: "O2 Produced (kg)",
        data: co2ChartResult.o2Data,
        borderColor: "#3B82F6",
        backgroundColor: "rgba(59, 130, 246, 0.08)",
        fill: true,
        tension: 0.3,
        borderDash: [4, 3],
        pointRadius: 2,
        pointBackgroundColor: "#3B82F6",
      },
    ]
  } : getDefaultChartData("CO2 Offset");

  const formatDateUs = (dateString) => {
    const options = { year: "numeric", month: "short" };
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", options);
  };

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p>{error}</p>
      </div>
    );
  }

  if (!clientData || clientData.length === 0) {
    return (
      <div style={styles.noDataContainer}>
        <p>No data found for this client.</p>
      </div>
    );
  }

  return (
    <div className="armasteel-dashboard">
      {isExporting && (
        <div className="pdf-export-overlay">
          <div className="pdf-loader-wrapper">
            <ReactLoading type="spin" color="#8BC34A" height={50} width={50} />
          </div>
          <p className="pdf-export-overlay-title">Generating User PDF Report...</p>
          <p className="pdf-export-overlay-subtitle">Preparing charts, statistics, and species distribution details</p>
        </div>
      )}
      <section className="bg-image pt-25 pb-25">
        <div className="container">
          <h2 className="Banner-Title">User Green Cover Impact Report</h2>
        </div>
      </section>

      <div style={{ justifyContent: 'center' }}>
        {clientData.userCoverPic && (
          <div
            className="cover-image"
            style={{
              backgroundImage: `url(${clientData.userCoverPic})`,
              height: "250px",
            }}
          ></div>)}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', marginTop: '25px' }}>
        <div style={{ flex: 1 }}></div>
        {/* <div className="user-climate-report-heading" style={{ margin: '0' }}>
          User Green Cover Impact Report
        </div> */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="export-pdf-btn"
            onClick={handleExportPDF}
            disabled={isExporting}
            style={{ margin: 0 }}
          >
            <FaFilePdf />{" "}
            <span>
              <strong style={{ color:"#0c3292" }}>{isExporting ? "Exporting..." : "Export Report"}</strong>
            </span>
          </button>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card impact-profile">
          <h2>User Profile</h2>
          <div className="profile-content">
            <img
              src={clientData.userProfilePic}
              alt="Armasteel Logo"
              className="company-logo"
            />
            <div className="company-info">
              <h3>
                {" "}
                {clientData?.full_name}
              </h3>
              <p>User Type: {clientData?.role_code}</p>
              <br />
              <p>Since: {formatDateUs(clientData.createdAt)}</p>

            </div>

          </div>
          <div
            className="share-buttons"
          >
            <FacebookShareButton url={window.location.href}>
              <FacebookIcon size={32} round />
            </FacebookShareButton>
            <TwitterShareButton url={window.location.href}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <button
              onClick={() => {
                navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => {
                    const shareUrl = `https://www.instagram.com/`;
                    window.open(shareUrl, "_blank");
                  })
                  .catch((err) => {
                    console.error("Failed to copy: ", err);
                  });
              }}
              style={{
                height: "32px",
                width: "32px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "50%",
                padding: "5px",
                backgroundColor: "#D93F7C",
              }}
            >
              <FaInstagram size={32} />
            </button>
            <WhatsappShareButton url={window.location.href}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <LinkedinShareButton url={window.location.href}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>
            <EmailShareButton url={window.location.href}>
              <EmailIcon size={32} round />
            </EmailShareButton>
          </div>
        </div>

        <div className="dashboard-card total-trees">
          <h2>Total Trees</h2>
          <div className="value">{treeData.length}</div>
          <Line
            ref={totalTreesChartRef}
            style={{ width: "60%", height: "60%" }}
            data={{
              labels: ["2021", "2024"],
              datasets: [
                {
                  data: [0, treeData.length],
                  borderColor: "#4CAF50",
                  tension: 0.1,
                },
              ],
            }}
            options={{
              plugins: { legend: { display: false } },
              scales: { y: { display: false }, x: { display: false } },
            }}
          />
        </div>

        <div className="dashboard-card plantation-status">
          <h2>Plantation Status</h2>
          <div className="status-circle">
            <Doughnut
              ref={plantationStatusChartRef}
              data={{
                labels: ["Planted", "Verified", "Geolocated"],
                datasets: [
                  {
                    data: [
                      clientData.totalTrees,
                      clientData.verifiedTrees,
                      clientData.geoLocatedTreesCount,
                    ],
                    backgroundColor: ["#4CAF50", "#FFA000", "#d30000"],
                  },
                ],
              }}
              options={{
                cutout: "85%",
                plugins: { legend: { display: false } },
              }}
            />
            <div className="status-text">
              <div className="value">{treeData.length / 1000}K</div>
              <div>Planted</div>
            </div>
          </div>
          <div className="status-legend">
            <div className="legend-item">
              <span className="dot planted"></span> Planted
            </div>
            <div className="legend-item">
              <span className="dot verified"></span> Verified
            </div>
            <div className="legend-item">
              <span className="dot geolocated"></span> Geolocated
            </div>
          </div>
        </div>

        <div className="dashboard-card average-weekly-trees">
          <h2>Average Weekly Trees</h2>
          <div className="value">
            {Math.round(
              calculateAverageWeeklyTrees(treeData).data.reduce(
                (a, b) => a + b,
                0
              ) / 7
            )}
          </div>
          <Bar
            ref={weeklyTreesChartRef}
            data={{
              labels: calculateAverageWeeklyTrees(treeData).labels,
              datasets: [
                {
                  label: "Trees Planted",
                  data: calculateAverageWeeklyTrees(treeData).data,
                  backgroundColor: "#4CAF50",
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: "Number of Trees",
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: "Day of Week",
                  },
                },
              },
            }}
          />
        </div>

        <div className="dashboard-card trees-by-month">
          <h2>Total Trees by Month</h2>
          <Line
            ref={treesByMonthChartRef}
            data={{
              labels: calculateTreesByYear(treeData).labels,
              datasets: [
                {
                  label: "Trees Planted",
                  data: calculateTreesByYear(treeData).data,
                  borderColor: "#4CAF50",
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                  fill: true,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false },
              },
              scales: { y: { beginAtZero: true } },
            }}
          />
        </div>

        <div className="dashboard-card co2-offset-by-month">
          <h2>Total Cumulative CO2 Offset &amp; O2 Produced by Month</h2>

          {co2ChartResult ? (
            <>
              <div style={{ display: 'flex', gap: '24px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Total CO2e Stored</p>
                  <p style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#4CAF50' }}>
                    {co2ChartResult.totalCO2e >= 1000
                      ? `${(co2ChartResult.totalCO2e / 1000).toFixed(2)} t`
                      : `${co2ChartResult.totalCO2e.toFixed(2)} kg`}
                  </p>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Total O2 Produced</p>
                  <p style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#3B82F6' }}>
                    {co2ChartResult.totalO2 >= 1000
                      ? `${(co2ChartResult.totalO2 / 1000).toFixed(2)} t`
                      : `${co2ChartResult.totalO2.toFixed(2)} kg`}
                  </p>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>Trees with DBH data </p>
                  <p style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#333' }}>
                    {co2ChartResult.treesWithDBH}
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>
                      {' '}/ {co2ChartResult.totalTrees} total
                    </span>
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#4CAF50', display: 'inline-block' }} />
                  <span style={{ fontSize: '11px', color: '#888' }}>CO2e Absorbed</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#3B82F6', display: 'inline-block' }} />
                  <span style={{ fontSize: '11px', color: '#888' }}>O2 Produced</span>
                </div>
              </div>

              <Line
                ref={co2ChartRef}
                data={co2ChartData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => {
                          const val = ctx.parsed.y;
                          const unit = val >= 1000 ? `${(val / 1000).toFixed(2)} t` : `${val} kg`;
                          return ` ${ctx.dataset.label.replace(' (kg)', '')}: ${unit}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: { display: true, text: "kg" }
                    },
                    x: {
                      title: { display: true, text: "Month" },
                      ticks: { maxTicksLimit: 12 }
                    }
                  }
                }}
              />

              <p style={{ fontSize: '10px', color: '#aaa', marginTop: '8px' }}>
                Biomass Formula Method (Chave et al. 2014) · Trees without DBH estimated at +0.05 cm/month growth · O2 = CO2e × 0.727
              </p>
            </>
          ) : (
            <>
              <Line
                data={getDefaultChartData("CO2 Offset")}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } }
                }}
              />
              <p style={{ fontSize: '11px', color: '#aaa', textAlign: 'center', marginTop: '8px' }}>
                No planting data available yet
              </p>
            </>
          )}
        </div>

        {(() => {
          const speciesMap = getSpeciesDataMap(treeData);
          const speciesKeys = Object.keys(speciesMap).sort((a, b) => a.localeCompare(b));
          if (speciesKeys.length === 0) return null;

          const totalSpeciesCount = speciesKeys.length;
          const totalTreesCount = Object.values(speciesMap).reduce((sum, i) => sum + i.count, 0);
          const totalCo2Val = Object.values(speciesMap).reduce((sum, i) => sum + (i.co2 || 0), 0);
          const totalO2Val = Object.values(speciesMap).reduce((sum, i) => sum + (i.o2 || 0), 0);

          const totalCo2Str = totalCo2Val >= 1000 ? `${(totalCo2Val / 1000).toFixed(2)} t` : `${totalCo2Val.toFixed(2)} kg`;
          const totalO2Str = totalO2Val >= 1000 ? `${(totalO2Val / 1000).toFixed(2)} t` : `${totalO2Val.toFixed(2)} kg`;

          return (
            <div className="species-distribution-card" style={{ gridColumn: "span 4", width: "100%", margin: "10px 0 20px 0" }}>
              <div className="species-card-header">
                <div className="species-card-title-group">
                  <h3 className="project-name" style={{ margin: 0 }}>
                    Species-wise Tree Distribution
                  </h3>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                    <span className="species-count-badge">
                      Total Species - {totalSpeciesCount}
                    </span>
                    <span className="species-count-badge">
                      Trees Count - {totalTreesCount}
                    </span>
                    <span className="species-count-badge">
                      CO₂ Sequestration - {totalCo2Str}
                    </span>
                    <span className="species-count-badge">
                      O2 produce - {totalO2Str}
                    </span>
                  </div>
                </div>
              </div>

              <Grid
                columns={[
                  {
                    header: "Sr. No.",
                    width: "12%",
                    align: "center",
                    render: (_, __, index) => index + 1,
                  },
                  {
                    header: "Species Name",
                    width: "40%",
                    align: "left",
                    sortable: true,
                    accessor: "speciesName",
                    render: (_, item) => (
                      <div className="species-item-cell">
                        <div className="species-icon-container">
                          <img
                            src={item.icon || '/images/icon/leaf.png'}
                            alt={item.speciesName}
                            className="species-img"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/images/icon/leaf.png';
                            }}
                          />
                        </div>
                        <span className="species-name-text">{item.speciesName}</span>
                      </div>
                    ),
                  },
                  {
                    header: "Tree Count",
                    width: "16%",
                    align: "center",
                    sortable: true,
                    accessor: "count",
                    render: (val) => <span className="species-count-pill">{val}</span>,
                  },
                  {
                    header: "CO₂ Sequestration",
                    width: "16%",
                    align: "right",
                    sortable: true,
                    accessor: "co2",
                    render: (val) => {
                      const co2Str = val >= 1000 ? `${(val / 1000).toFixed(2)} t` : `${(val || 0).toFixed(2)} kg`;
                      return <span className="co2-metric-pill">{co2Str}</span>;
                    },
                  },
                  {
                    header: "O2 produce",
                    width: "16%",
                    align: "right",
                    sortable: true,
                    accessor: "o2",
                    render: (val) => {
                      const o2Str = val >= 1000 ? `${(val / 1000).toFixed(2)} t` : `${(val || 0).toFixed(2)} kg`;
                      return <span className="o2-metric-pill">{o2Str}</span>;
                    },
                  },
                ]}
                data={speciesKeys.map((key) => ({
                  speciesName: key,
                  ...speciesMap[key],
                }))}
                footer={[
                  { content: "" },
                  { content: <span style={{ fontWeight: 700, letterSpacing: "0.5px",color:"black" }}>TOTAL</span> },
                  {
                    content: <span className="species-count-pill total-pill">{totalTreesCount}</span>,
                    align: "center",
                  },
                  {
                    content: <span className="co2-metric-pill total-co2">{totalCo2Str}</span>,
                    align: "right",
                  },
                  {
                    content: <span className="o2-metric-pill total-o2">{totalO2Str}</span>,
                    align: "right",
                  },
                ]}
                variant="forest"
                rowKey="speciesName"
                maxHeight="320px"
              />
            </div>
          );
        })()}

        <div className="dashboard-card project-map">
          <h2>Project Locations & Tree Tracking</h2>
          {/* New Map */}
          <MapContainer
            center={mockData.position}
            zoom={4}
            style={{ height: "50vh", width: "100%" }}
          >
            <TileLayer url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}" />
            <MarkerClusterGroup>
              {treeData.map((tree) => (
                <Marker
                  key={tree.id}
                  position={[tree.lat, tree.lng]}
                  icon={tree.isVerified ? verifiedIcon : unverifiedIcon}
                >
                  <Popup>
                    <div style={{
                      display: 'flex',
                      minWidth: '400px',
                      maxWidth: '600px',
                      gap: '4px',
                      padding: '0'
                    }}>
                      <div style={{ width: '40%' }}>
                        <a
                          target="_blank"
                          className="search-more-info-btn"
                          href={`/plantprofile/${tree.id}`}
                          rel="noreferrer"
                          title="More Info"
                        >
                          <img
                            src={tree.treeimageUrl}
                            alt={tree.imageName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'scale-down',
                              borderRadius: '4px'
                            }}
                          /></a>
                      </div>
                      <div style={{
                        width: '40%',
                        fontSize: '8px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          <a
                            target="_blank"
                            className="search-more-info-btn"
                            href={`/plantprofile/${tree.tree_id}`}
                            rel="noreferrer"
                            title="More Info"
                          >
                            <p style={{
                              margin: 0,
                              lineHeight: '1'


                            }}>{tree.treeName}</p>
                          </a>
                          {tree.isVerified ? (
                            <FaCheckCircle style={{
                              color: '#16a34a',
                              fontSize: '12px'
                            }} />
                          ) : (<FaCheckCircle style={{
                            color: 'yellow',
                            fontSize: '12px'
                          }} />)}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '1px' }}>
                          <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="TreeId">
                            <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaTree style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                            {tree.tree_id}
                          </p>
                          <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Uploaded By">
                            <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaUserAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                            {tree.full_name}
                          </p>
                          <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Uploaded On">
                            <span style={{ fontWeight: 600, color: 'black' }}><FaCalendarPlus style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                            {tree.createdAt && !isNaN(new Date(tree.createdAt)) ? new Date(tree.createdAt).toLocaleDateString() : 'N/A'}
                          </p>
                          <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Planted On">
                            <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaCalendarAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                            {tree.plantedOn && !isNaN(new Date(tree.plantedOn)) ? new Date(tree.plantedOn).toLocaleDateString() : 'N/A'}
                          </p>
                          <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Species">
                            <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaPagelines style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                            {getSpeciesLabel(tree)}
                          </p>
                          <p style={{ margin: 0, fontSize: '10px', lineHeight: '1', padding: '0' }} title="Age">
                            <span style={{ fontWeight: 600, color: 'black', fontSize: '10px' }}><FaClock style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span>{' '}
                            {tree.age}
                          </p>

                          <p style={{
                            margin: 0,
                            overflow: 'hidden', // Prevents overflow
                            textOverflow: 'ellipsis', // Adds ellipsis if the text overflows
                            display: '-webkit-box', // Ensures the flexbox container
                            WebkitBoxOrient: 'vertical', // Sets the text to be vertical
                            WebkitLineClamp: 2, // Limits the lines before truncation, can adjust the number of lines
                            wordBreak: 'break-word', // Breaks long words onto the next line
                            fontSize: '10px',
                            lineHeight: '1',
                            padding: '0'
                          }} title="Location">
                            <span style={{ fontWeight: 600, color: 'black' }}><FaMapMarkerAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /></span> {tree.address}

                          </p>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

const styles = {
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
};
